import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight, LuLoaderCircle } from 'react-icons/lu';
import { useTranslation } from '../../../i18n/LanguageContext';

const NAV_BUTTON = [
  'tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-500 tw-transition-colors',
  'hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-35 disabled:hover:tw-bg-transparent',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
  'dark:tw-text-slate-400 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white',
].join(' ');

// Up to five page numbers around the current page
const getPageNumbers = (currentPage, totalPages) => Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
  if (totalPages <= 5 || currentPage <= 3) return i + 1;
  if (currentPage >= totalPages - 2) return totalPages - 4 + i;
  return currentPage - 2 + i;
});

/**
 * Which rows are on screen, and page navigation under the table. `compact`
 * (narrow screens) shows "2 / 11" between the arrows instead of page numbers.
 */
const TablePagination = ({
  rangeStart,
  rangeEnd,
  resultCount,
  totalEmployees,
  isFilterActive,
  currentPage,
  totalPages,
  isLoading,
  compact,
  onPage,
}) => {
  const { t } = useTranslation();
  const atStart = currentPage <= 1;
  const atEnd = currentPage >= totalPages;

  return (
    <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-gap-2 tw-border-t tw-border-slate-200/70 tw-px-4 tw-py-2.5 sm:tw-flex-row sm:tw-justify-between dark:tw-border-white/10">
      <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400" aria-live="polite">
        {resultCount === 0 ? (
          <span className="tw-inline">{t('employees.pagination.noResults')}</span>
        ) : (
          <span className="tw-inline">
            <strong className="tw-font-semibold tw-text-slate-900 tw-tabular-nums dark:tw-text-white">{rangeStart}–{rangeEnd}</strong>
            {' '}
            {t('employees.pagination.rangeOf', { total: resultCount })}
            {isFilterActive && (
              <span className="tw-hidden sm:tw-inline"> · {t('employees.pagination.ofTotal', { total: totalEmployees })}</span>
            )}
          </span>
        )}
        {isLoading && (
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-teal-600 dark:tw-text-teal-300">
            <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
            {t('employees.pagination.loading')}
          </span>
        )}
      </p>

      {/* Always rendered: a footer that changed height would change how many rows fit */}
      <nav aria-label={t('employees.pagination.pages')} className="tw-flex tw-items-center tw-gap-0.5">
        <button type="button" onClick={() => onPage(1)} disabled={atStart} aria-label={t('employees.pagination.first')} title={t('employees.pagination.first')} className={NAV_BUTTON}>
          <LuChevronsLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
        <button type="button" onClick={() => onPage(currentPage - 1)} disabled={atStart} aria-label={t('employees.pagination.previous')} title={t('employees.pagination.previous')} className={NAV_BUTTON}>
          <LuChevronLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
        {compact && (
          <span className="tw-min-w-[4.5rem] tw-px-2 tw-text-center tw-text-sm tw-font-semibold tw-text-slate-700 tw-tabular-nums dark:tw-text-slate-200">
            {currentPage} / {totalPages}
          </span>
        )}
        {!compact && getPageNumbers(currentPage, totalPages).map((page) => {
          const active = page === currentPage;
          return (
            <button
              key={page}
              type="button"
              onClick={() => onPage(page)}
              aria-current={active ? 'page' : undefined}
              aria-label={t('employees.pagination.page', { page })}
              className={`tw-h-9 tw-min-w-[2.25rem] tw-rounded-full tw-px-2 tw-text-sm tw-font-semibold tw-tabular-nums tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                active
                  ? 'tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-md tw-shadow-teal-500/30'
                  : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white'
              }`}
            >
              {page}
            </button>
          );
        })}
        <button type="button" onClick={() => onPage(currentPage + 1)} disabled={atEnd} aria-label={t('employees.pagination.next')} title={t('employees.pagination.next')} className={NAV_BUTTON}>
          <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
        <button type="button" onClick={() => onPage(totalPages)} disabled={atEnd} aria-label={t('employees.pagination.last')} title={t('employees.pagination.last')} className={NAV_BUTTON}>
          <LuChevronsRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
      </nav>
    </div>
  );
};

TablePagination.propTypes = {
  rangeStart: PropTypes.number.isRequired,
  rangeEnd: PropTypes.number.isRequired,
  resultCount: PropTypes.number.isRequired,
  totalEmployees: PropTypes.number.isRequired,
  isFilterActive: PropTypes.bool,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  compact: PropTypes.bool,
  onPage: PropTypes.func.isRequired,
};

export default TablePagination;
